"use client";

import React, { useEffect, useRef, useState } from "react";
import Script from "next/script";
import NextImage from "@components/Image/NextImage";
import { useAppContext } from "@components/Provider";
import { RenderHomePageSchema } from "helpers/Helper";
import EBBLandingSignupForm from "./EBBLandingSignupForm";
import {
  ArtImage,
  LandingHeader,
  LandingSignupModal,
  Testimonials,
  renderAccentText,
} from "./EBBLandingShared";
import {
  extractHomepageClosing,
  extractHomepageDeals,
  extractHomepageFeatures,
  extractHomepageMedia,
  extractHomepageSignup,
  extractHomepageTestimonials,
  formatBadgePrice,
  getLandingPagePost,
} from "./ebbLandingPageUtils";
import styles from "./EBBLandingPage.module.css";

const BookCover = ({ book, className, priority = false, onSignupClick }) => {
  const href = book.link || "";
  const cover = (
    <div className={`${styles.bookCover} ${className || ""}`}>
      {book.price ? (
        <span className={styles.priceBadge}>{formatBadgePrice(book.price)}</span>
      ) : null}
      <NextImage
        ImageUrl={book.imageUrl}
        Alt_Text={book.title || "Ebook cover"}
        Title={book.title}
        width={120}
        height={180}
        isLazyload={!priority}
        fetchpriority={priority}
        style={{ width: "100%", height: "auto" }}
      />
    </div>
  );

  if (href) {
    return (
      <a href={href} className={styles.bookLink}>
        {cover}
      </a>
    );
  }
  if (onSignupClick) {
    return (
      <button type="button" className={styles.bookLink} onClick={onSignupClick}>
        {cover}
      </button>
    );
  }
  return cover;
};

const DealsCarousel = ({ books, onBookSignup }) => {
  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const slides = books;

  const getStep = () => {
    const node = scrollerRef.current;
    if (!node?.firstElementChild) return 0;
    const gap = parseFloat(window.getComputedStyle(node).gap) || 16;
    return node.firstElementChild.getBoundingClientRect().width + gap;
  };

  const syncPage = () => {
    const node = scrollerRef.current;
    const step = getStep();
    if (!node || !step) return;
    setPage(Math.round(node.scrollLeft / step));
  };

  const scrollToPage = (index) => {
    const node = scrollerRef.current;
    const step = getStep();
    if (!node || !step) return;
    node.scrollTo({ left: step * index, behavior: "smooth" });
    setPage(index);
  };

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return undefined;
    node.addEventListener("scroll", syncPage, { passive: true });
    return () => node.removeEventListener("scroll", syncPage);
  }, [slides.length]);

  useEffect(() => {
    if (paused || slides.length < 2) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const desktop = window.matchMedia("(min-width: 768px)").matches;
    if (reduceMotion || !desktop) return undefined;

    const id = window.setInterval(() => {
      const node = scrollerRef.current;
      const step = getStep();
      if (!node || !step) return;
      const maxScroll = node.scrollWidth - node.clientWidth;
      const nextLeft = node.scrollLeft + step;
      node.scrollTo({
        left: nextLeft >= maxScroll - 8 ? 0 : nextLeft,
        behavior: "smooth",
      });
    }, 4000);

    return () => window.clearInterval(id);
  }, [paused, slides.length]);

  return (
    <div
      className={styles.carouselWrap}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <div className={styles.carouselTrack} ref={scrollerRef}>
        {slides.map((book, index) => (
          <div className={styles.carouselCard} key={`${book.isbn || book.title}-${index}`}>
            <BookCover book={book} onSignupClick={onBookSignup} />
          </div>
        ))}
      </div>
      <div className={styles.dots} role="tablist" aria-label="Recent ebook deals">
        {slides.map((book, index) => (
          <button
            key={`deal-dot-${index}`}
            type="button"
            className={`${styles.dot} ${page === index ? styles.dotActive : ""}`}
            aria-label={`Show deal ${index + 1}`}
            onClick={() => scrollToPage(index)}
          />
        ))}
      </div>
    </div>
  );
};

const EBBLandingPage = (props) => {
  const { data, host: contextHost, siteId: contextSiteId } = useAppContext();
  const post = getLandingPagePost(props);
  const host = contextHost || props.host;
  const siteId = contextSiteId || props.siteId;
  const siteSettings = data?.SiteSettings || {};
  const {
    header: signupHeader,
    modalHeader: signupModalHeader,
    buttonText: ctaText,
    signUpLocation,
    pageNewsletter,
    isModal: isModalSignup,
    newsletterProps,
    dealsNewsletter,
    dealsModalHeader,
  } = extractHomepageSignup(post);
  const { heroDesktop, heroMobile } = extractHomepageMedia({
    heroDesktopFile: props.ebbHeroBannerDesktop,
    heroMobileFile: props.ebbHeroBannerMobile,
  });
  const features = extractHomepageFeatures(post);
  const { header: dealsHeader, subheader: dealsSubheader, books } = extractHomepageDeals(post);
  const quotes = extractHomepageTestimonials(post);
  const closing = extractHomepageClosing(post);
  const siteLogo =
    siteSettings.primary_site_logo_svg_black ||
    siteSettings.primary_site_logo_svg ||
    siteSettings.primary_site_logo;
  const siteName = siteSettings.site_name || "Early Bird Books";
  const heroRef = useRef(null);
  const closingRef = useRef(null);
  const [heroPast, setHeroPast] = useState(false);
  const [closingInView, setClosingInView] = useState(false);
  const [signupModal, setSignupModal] = useState(null);
  const showStickySignup = heroPast && !closingInView;
  const featureClassNames = [styles.featureBargain, styles.featureGenres, styles.featureRetailers];

  useEffect(() => {
    const hero = heroRef.current;
    const closing = closingRef.current;
    if (!hero) return undefined;

    let frame = 0;

    const update = () => {
      frame = 0;
      const headerRow = document.querySelector("[data-ebb-landing-header-row]");
      const headerBottom = headerRow ? headerRow.getBoundingClientRect().bottom : 64;
      const heroCard = hero.querySelector("[data-ebb-hero-card]");
      const pastHero = (heroCard || hero).getBoundingClientRect().bottom <= headerBottom + 8;
      const closingTop = closing ? closing.getBoundingClientRect().top : Infinity;
      const arrivedAtClosing = closingTop < Math.max(headerBottom + 48, window.innerHeight * 0.4);
      setHeroPast(pastHero);
      setClosingInView(arrivedAtClosing);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("scroll", scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(hero);
    resizeObserver.observe(document.documentElement);
    if (closing) resizeObserver.observe(closing);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("scroll", scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollToSignup = () => {
    const target = document.getElementById("homepage-signup");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onCtaClick = () => {
    if (isModalSignup) {
      setSignupModal("page");
      return;
    }
    scrollToSignup();
  };

  return (
    <div className={styles.page}>
      <LandingHeader
        siteLogo={siteLogo}
        siteName={siteName}
        showStickySignup={showStickySignup}
        onCtaClick={onCtaClick}
        headline={signupHeader}
        ctaText={ctaText}
        signUpLocation={signUpLocation}
        pageNewsletter={pageNewsletter}
        Newsletter_Settings={siteSettings.newsletter_settings}
        postType={post?.post_type}
      />

      <section className={styles.hero} ref={heroRef}>
        <ArtImage
          desktopSrc={heroDesktop}
          mobileSrc={heroMobile}
          className={styles.heroBanner}
          fetchPriority="high"
        />
        <div className={styles.heroCard} data-ebb-hero-card="true">
          {signupHeader ? <h1>{signupHeader}</h1> : null}
          <EBBLandingSignupForm
            variant="hero"
            formId="homepage-hero-form"
            signUpLocation={signUpLocation}
            newsletterProps={pageNewsletter}
            Newsletter_Settings={siteSettings.newsletter_settings}
            postType={post?.post_type}
          />
        </div>
      </section>

      {features.length > 0 ? (
        <section className={styles.features}>
          {features.map((feature, index) => (
            <article
              className={`${styles.feature} ${featureClassNames[index] || ""}`}
              key={`feature-${index}`}
            >
              {feature.imageUrl ? (
                <div className={styles.featureVisual}>
                  <img src={feature.imageUrl} alt="" className={styles.pointImage} />
                </div>
              ) : null}
              {feature.heading ? <h2>{renderAccentText(feature.heading)}</h2> : null}
              {feature.body ? <p>{feature.body}</p> : null}
            </article>
          ))}
        </section>
      ) : null}

      {ctaText ? (
        <div className={styles.ctaRow}>
          <button type="button" className={styles.coralCta} onClick={onCtaClick}>
            {ctaText}
          </button>
        </div>
      ) : null}

      {books.length > 0 ? (
        <section className={styles.deals}>
          {dealsHeader ? <h2>{dealsHeader}</h2> : null}
          {dealsSubheader ? <p className={styles.dealsSub}>{dealsSubheader}</p> : null}
          <DealsCarousel
            books={books}
            onBookSignup={dealsNewsletter ? () => setSignupModal("deals") : undefined}
          />
        </section>
      ) : null}

      {ctaText ? (
        <div className={styles.ctaRow}>
          <button type="button" className={styles.coralCta} onClick={onCtaClick}>
            {ctaText}
          </button>
        </div>
      ) : null}

      {quotes.length > 0 ? <Testimonials quotes={quotes} /> : null}

      <section className={styles.closing} id="homepage-signup" ref={closingRef}>
        <ArtImage
          desktopSrc={closing.imageDesktop}
          mobileSrc={closing.imageMobile}
          className={styles.closingBanner}
          alt=""
        />
        {closing.header ? <h2>{renderAccentText(closing.header)}</h2> : null}
        <EBBLandingSignupForm
          variant="closing"
          formId="homepage-closing-form"
          signUpLocation={signUpLocation}
          newsletterProps={pageNewsletter}
          Newsletter_Settings={siteSettings.newsletter_settings}
          postType={post?.post_type}
        />
      </section>
      {isModalSignup ? (
        <LandingSignupModal
          isOpen={signupModal === "page"}
          onClose={() => setSignupModal(null)}
          formId="homepage-modal-form"
          headline={signupModalHeader || signupHeader}
          signUpLocation={signUpLocation}
          newsletterProps={newsletterProps}
          Newsletter_Settings={siteSettings.newsletter_settings}
          postType={post?.post_type}
        />
      ) : null}
      {dealsNewsletter ? (
        <LandingSignupModal
          isOpen={signupModal === "deals"}
          onClose={() => setSignupModal(null)}
          formId="homepage-deals-modal-form"
          headline={dealsModalHeader || signupModalHeader || signupHeader}
          signUpLocation={dealsNewsletter.signUpLocation || signUpLocation}
          newsletterProps={dealsNewsletter}
          Newsletter_Settings={siteSettings.newsletter_settings}
          postType={post?.post_type}
        />
      ) : null}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={RenderHomePageSchema(siteSettings, post, host, siteId)}
      />
      <Script
        strategy="lazyOnload"
        type="text/javascript"
        data-cfasync="false"
        dangerouslySetInnerHTML={{
          __html: `
            var blogherads = blogherads || {};
            blogherads.adq = blogherads.adq || [];
            blogherads.adq.push(function() {
              blogherads.disableAds();
            });
          `,
        }}
      />
    </div>
  );
};

export default EBBLandingPage;
